// src/contracts/ops/OpsEvents.ts
//
// SynkTribe Ops Events (v1)
// -------------------------
// Purpose: provide a single, safe event spine for the Broadcast Ops Dashboard.
// - NO raw payloads
// - NO PII
// - Bounded / summarized data only
// - Designed for sampling + low overhead
//

import type {
  OpsBand,
  OpsHealth,
  OpsSeverity,
  CapabilityId,
  OpsActorId,
} from "./OpsDashboardModel";

import type { SynkWiringManifestV1, SynkWiringManifestSummaryV1 } from "./WiringManifest";

/** Root Ops bus namespace */
export const SYNK_OPS_NAMESPACE = "synktribe:ops" as const;

/** Emitted once per install pass (or on app ready) */
export const SYNK_WIRING_READY_EVENT = "synktribe:wiring:ready" as const;

/** Generic ops metric/counter update */
export const SYNK_OPS_METRIC_EVENT = "synktribe:ops:metric" as const;

/** Contract / boundary violation summary */
export const SYNK_OPS_VIOLATION_EVENT = "synktribe:ops:violation" as const;

/** Capability lifecycle and status updates */
export const SYNK_OPS_CAPABILITY_EVENT = "synktribe:ops:capability" as const;

/** Operator-issued control actions */
export const SYNK_OPS_ACTION_EVENT = "synktribe:ops:action" as const;

/** Timeline evidence events (proof / audit) */
export const SYNK_OPS_TIMELINE_EVENT = "synktribe:ops:timeline" as const;

/** Optional snapshot request/response (useful for embeds / remote consoles) */
export const SYNK_OPS_SNAPSHOT_REQUEST_EVENT =
  "synktribe:ops:snapshot:request" as const;
export const SYNK_OPS_SNAPSHOT_EVENT = "synktribe:ops:snapshot" as const;

/** -----------------------------
 * Payloads (v1)
 * ------------------------------ */

export interface SynkWiringReadyDetailV1 {
  v: 1;
  at: number;

  buildId: string;
  manifestChecksum: string;
  env: "prod" | "staging" | "dev" | "unknown";
  tier: "public" | "creator" | "enterprise" | "regulated" | "unknown";

  authoritySource: {
    kind: "user" | "service" | "unknown";
    id: string;
  };

  installed: Array<{
    id: CapabilityId;
    tierRequired: "public" | "creator" | "enterprise" | "regulated";
    safety: "witness" | "authority" | "transformer";
  }>;

  /** Optional: wiring manifest summary for dashboards */
  manifestSummary?: SynkWiringManifestSummaryV1;

  /** Optional: full manifest (still witness-safe, but larger) */
  manifest?: SynkWiringManifestV1;
}

export type OpsMetricKindV1 = "flow" | "latency" | "health" | "custom";

/**
 * Generic metric update (band-first).
 * - Use "flow" for counts/drops
 * - Use "latency" for p95 + band
 */
export interface SynkOpsMetricDetailV1 {
  v: 1;
  at: number;

  kind: OpsMetricKindV1;

  /** Metric name (safe, non-payload). */
  name: string;

  /** Band classification for ops. */
  band?: OpsBand;

  /** Numeric value (safe). For counts, set value=count. */
  value?: number;

  /** Window size seconds when applicable (flow counters). */
  windowSec?: number;

  /** Channel/event name when applicable (flow counters). */
  channel?: string;

  /** Optional capability context (no payload). */
  capabilityId?: CapabilityId;

  /** Optional p95 ms for latency kind. */
  p95Ms?: number;

  /** Optional dropped count (flow). */
  dropped?: number;
}

export interface SynkOpsViolationDetailV1 {
  v: 1;
  at: number;

  severity: OpsSeverity;

  channel: string; // e.g. "omnicast:surface:intent"
  where: string; // e.g. capability id or module name
  reason: string; // e.g. "schema_invalid" | "boundary_breach"
  schema?: string; // optional schema name
}

export type OpsCapabilityEventKindV1 =
  | "installed"
  | "uninstalled"
  | "blocked"
  | "faulted"
  | "degraded"
  | "ok"
  | "heartbeat";

export interface SynkOpsCapabilityDetailV1 {
  v: 1;
  at: number;

  kind: OpsCapabilityEventKindV1;

  id: CapabilityId;

  tierRequired: "public" | "creator" | "enterprise" | "regulated";
  safety: "witness" | "authority" | "transformer";

  status: OpsHealth;

  /** Short reason code only. */
  reason?: string;
}

export type OpsActionTypeV1 =
  | "disable_capability"
  | "enable_capability"
  | "force_fallback"
  | "clear_fallback";

export interface SynkOpsActionDetailV1 {
  v: 1;
  at: number;

  actor: OpsActorId;

  action: OpsActionTypeV1;

  /** Target capability (when relevant). */
  target?: CapabilityId;

  /** Human-entered reason (keep short; no PII). */
  reason?: string;
}

export type OpsTimelineKindV1 =
  | "go_live"
  | "capability_installed"
  | "capability_uninstalled"
  | "capability_blocked"
  | "capability_faulted"
  | "policy_decision"
  | "ops_action"
  | "export_proof_bundle";

export interface SynkOpsTimelineDetailV1 {
  v: 1;
  at: number;

  kind: OpsTimelineKindV1;

  /** Strictly small, redacted details. */
  detail: Record<string, string | number | boolean | null | undefined>;
}

export interface SynkOpsSnapshotRequestDetailV1 {
  v: 1;
  at: number;
  /** Optional requester id (opaque). */
  requester?: string;
}

export interface SynkOpsSnapshotDetailV1 {
  v: 1;
  at: number;
  /** The whole ops state object can be attached here if desired. */
  state: unknown;
}
